/**
 * Static Commands for Command Palette
 *
 * Defines the static navigation commands and workflow shortcuts for Stream.
 * Each command has a single-letter chord for quick access after Cmd+K.
 */

export interface StaticCommand {
  /** Unique identifier. */
  id: string;
  /** Single-letter chord for quick access. */
  chord: string;
  /** Human-readable label. */
  label: string;
  /** Navigation path (if navigation command). */
  href?: string;
  /** Action identifier (if action command). */
  action?: string;
  /** Search keywords for filtering. */
  keywords?: string[];
}

/**
 * Static commands for Stream.
 *
 * The single-letter prefix is the suggested chord - after Cmd+K, pressing
 * the letter activates the command without typing the full word.
 */
export const STATIC_COMMANDS: StaticCommand[] = [
  // Navigation - Observe
  {
    id: "go-to-events",
    chord: "E",
    label: "Go to Events",
    href: "/events",
    keywords: ["events", "stream", "live", "observe"],
  },

  // Navigation - Operate
  {
    id: "go-to-queue",
    chord: "Q",
    label: "Go to Queue",
    href: "/queue",
    keywords: ["queue", "approvals", "pending"],
  },
  {
    id: "go-to-memory",
    chord: "M",
    label: "Go to Memory",
    href: "/memory",
    keywords: ["memory", "facts", "knowledge"],
  },
  {
    id: "go-to-agents",
    chord: "F",
    label: "Go to Agents",
    href: "/agents",
    keywords: ["agents", "agents", "bots", "ai"],
  },
  // Navigation - Settings
  {
    id: "go-to-settings",
    chord: "P",
    label: "Settings",
    href: "/settings",
    keywords: ["settings", "preferences", "options", "config"],
  },
  // Actions
  {
    id: "toggle-session-panel",
    chord: "S",
    label: "Toggle Session Panel",
    action: "toggle-session-panel",
    keywords: ["session", "panel", "sidebar", "toggle"],
  },
  {
    id: "show-shortcuts",
    chord: "?",
    label: "Show Keyboard Shortcuts",
    action: "show-shortcuts",
    keywords: ["shortcuts", "keyboard", "keys", "help"],
  },
  // External Integration Health commands — Task 2578
  {
    id: "go-to-integrations",
    chord: "V",
    label: "Go to Integrations",
    href: "/integrations",
    keywords: ["integrations", "providers", "vendors", "stripe", "mailgun", "gmail", "oauth", "webhooks", "external"],
  },
  {
    id: "integrations.refresh",
    chord: "",
    label: "Integrations: refresh",
    action: "integrations.refresh",
    keywords: ["refresh", "reload", "integrations", "update"],
  },
  {
    id: "integrations.test-all",
    chord: "",
    label: "Integrations: test all providers",
    action: "integrations.test-all",
    keywords: ["test", "synthetic", "all", "integrations", "providers"],
  },
  {
    id: "integrations.test-stripe",
    chord: "",
    label: "Integrations: test Stripe",
    action: "integrations.test-stripe",
    keywords: ["test", "stripe", "payments", "integration"],
  },
  {
    id: "integrations.test-mailgun",
    chord: "",
    label: "Integrations: test Mailgun",
    action: "integrations.test-mailgun",
    keywords: ["test", "mailgun", "email", "integration"],
  },
  {
    id: "integrations.test-gmail",
    chord: "",
    label: "Integrations: test Gmail",
    action: "integrations.test-gmail",
    keywords: ["test", "gmail", "google", "oauth", "integration"],
  },
  {
    id: "integrations.export",
    chord: "",
    label: "Integrations: export errors as CSV",
    action: "integrations.export",
    keywords: ["export", "csv", "download", "integrations", "errors"],
  },
  // CSV Export Actions
  {
    id: "export-customers",
    chord: "",
    label: "Export Customers to CSV",
    action: "customers.export",
    keywords: ["export", "csv", "customers", "download"],
  },
  {
    id: "export-events",
    chord: "",
    label: "Export Events to CSV",
    action: "events.export",
    keywords: ["export", "csv", "events", "download"],
  },
  {
    id: "export-sessions",
    chord: "",
    label: "Export Sessions to CSV",
    action: "sessions.export",
    keywords: ["export", "csv", "sessions", "download"],
  },
  {
    id: "export-purchases",
    chord: "",
    label: "Export Purchases to CSV",
    action: "purchases.export",
    keywords: ["export", "csv", "purchases", "download"],
  },
  {
    id: "export-catalog",
    chord: "",
    label: "Export Catalog to CSV",
    action: "catalog.export",
    keywords: ["export", "csv", "catalog", "download"],
  },
  {
    id: "export-engagement",
    chord: "",
    label: "Export Engagement to CSV",
    action: "engagement.export",
    keywords: ["export", "csv", "engagement", "download"],
  },
  // Artifact Actions
  {
    id: "artifacts.upload",
    chord: "",
    label: "Artifacts: Upload Files",
    action: "artifacts.upload",
    keywords: ["artifacts", "upload", "files", "drop", "attach"],
  },
  // Sessions Comparison (task 2621)
  {
    id: "sessions-compare",
    chord: "",
    label: "Sessions: Compare Selected",
    action: "sessions.compare",
    keywords: ["sessions", "compare", "side-by-side", "diff", "comparison"],
  },
];

/**
 * Filter commands by a search query.
 * Matches against chord, label, and keywords.
 */
export function filterCommands(commands: StaticCommand[], query: string): StaticCommand[] {
  if (!query.trim()) {
    return commands;
  }

  const lowerQuery = query.toLowerCase();

  return commands.filter((cmd) => {
    // Match by chord (exact single character)
    if (cmd.chord.toLowerCase() === lowerQuery) {
      return true;
    }

    // Match by label
    if (cmd.label.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match by keywords
    if (cmd.keywords?.some((kw) => kw.toLowerCase().includes(lowerQuery))) {
      return true;
    }

    return false;
  });
}
